import { themeColor } from './color';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    inputLabel: React.CSSProperties;
    inputValue: React.CSSProperties;
    bodyBold: React.CSSProperties;
    prompt: React.CSSProperties;
    bodyMd: React.CSSProperties;
    bodyXs: React.CSSProperties;
    bodySm: React.CSSProperties;
    bodyMediumWeight: React.CSSProperties;
    bodyLg: React.CSSProperties;
    bodyXlSemibold: React.CSSProperties;
    bodySmMedium: React.CSSProperties;
    bodySmSemibold: React.CSSProperties;
    bodySmBold: React.CSSProperties;
    bodyLgMedium: React.CSSProperties;
    bodyXsMedium: React.CSSProperties;
    bodyXlBold: React.CSSProperties;
    bodyLgBold: React.CSSProperties;
    headingXs: React.CSSProperties;
    headingSm: React.CSSProperties;
    headingMd: React.CSSProperties;
    headingLg: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    inputLabel?: React.CSSProperties;
    inputValue?: React.CSSProperties;
    bodyBold?: React.CSSProperties;
    prompt?: React.CSSProperties;
    bodyMd?: React.CSSProperties;
    bodyXs?: React.CSSProperties;
    bodySm?: React.CSSProperties;
    bodyMediumWeight?: React.CSSProperties;
    bodyLg?: React.CSSProperties;
    bodyXlSemibold?: React.CSSProperties;
    bodySmMedium?: React.CSSProperties;
    bodySmSemibold?: React.CSSProperties;
    bodySmBold?: React.CSSProperties;
    bodyLgMedium?: React.CSSProperties;
    bodyXsMedium?: React.CSSProperties;
    bodyXlBold?: React.CSSProperties;
    bodyLgBold?: React.CSSProperties;
    headingXs?: React.CSSProperties;
    headingSm?: React.CSSProperties;
    headingMd?: React.CSSProperties;
    headingLg?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    inputLabel: true;
    inputValue: true;
    bodyBold: true;
    prompt: true;
    bodyMd: true;
    bodyXs: true;
    bodySm: true;
    bodyMediumWeight: true;
    bodyLg: true;
    bodyXlSemibold: true;
    bodySmMedium: true;
    bodySmSemibold: true;
    bodySmBold: true;
    bodyLgMedium: true;
    bodyXsMedium: true;
    bodyXlBold: true;
    bodyLgBold: true;
    headingXs: true;
    headingSm: true;
    headingMd: true;
    headingLg: true;
  }
}

export const fontFamilyBody = ['Work Sans', 'sans-serif'].join(',');
export const fontFamilyHeader = ['Lora', 'serif'].join(',');

export const fontWeightLight = 300;
export const fontWeightRegular = 400;
export const fontWeightMedium = 500;
export const fontWeightSemiBold = 600;
export const fontWeightBold = 700;

export const LoraFont = {
  fontFamily: fontFamilyHeader,
  fontStyle: 'normal',
  fontWeight: fontWeightRegular,
};

export const HeaderItalic = {
  fontFamily: fontFamilyHeader,
  fontStyle: 'italic',
  fontWeight: fontWeightSemiBold,
};

export const WorkSansFont = {
  fontFamily: fontFamilyBody,
  fontStyle: 'normal',
  fontWeight: fontWeightRegular,
};

export const typography = {
  fontFamily: fontFamilyBody,
  fontWeightLight,
  fontWeightRegular,
  fontWeightMedium,
  fontWeightBold,

  h1: {
    ...HeaderItalic,
    fontSize: '2.125rem',
    lineHeight: 1.167,
    letterSpacing: '-1.5px',
    color: themeColor.palette.text.title,
  },
  h2: {
    ...HeaderItalic,
    fontSize: '1.5rem',
    lineHeight: 1.2,
    letterSpacing: '-0.5px',
    color: themeColor.palette.text.title,
  },
  h3: {
    ...HeaderItalic,
    fontSize: '1.25rem',
    lineHeight: 1.167,
    letterSpacing: '0px',
    color: themeColor.palette.text.title,
  },
  h4: {
    ...WorkSansFont,
    fontWeight: fontWeightMedium,
    fontSize: '1.125rem',
    lineHeight: 1.35,
    letterSpacing: '0.15px',
    color: themeColor.palette.text.title,
  },
  h5: {
    ...WorkSansFont,
    fontWeight: fontWeightMedium,
    fontSize: '1rem',
    lineHeight: 1.4,
    letterSpacing: '0.15px',
    color: themeColor.palette.text.title,
  },
  h6: {
    ...WorkSansFont,
    fontWeight: fontWeightMedium,
    fontSize: '0.875rem',
    lineHeight: 1.4,
    letterSpacing: '0.1px',
    color: themeColor.palette.text.title,
  },

  subtitle1: {
    ...WorkSansFont,
    fontSize: '1rem',
    lineHeight: 1.4,
    letterSpacing: '0.15px',
    color: themeColor.palette.text.secondary,
  },
  subtitle2: {
    ...WorkSansFont,
    fontWeight: fontWeightMedium,
    fontSize: '0.875rem',
    lineHeight: 1.4,
    letterSpacing: '0.1px',
    color: themeColor.palette.text.secondary,
  },
  // A form question prompt: subtitle2 sizing bumped to 1rem so the question
  // the user is answering reads slightly larger than supporting copy.
  prompt: {
    ...WorkSansFont,
    fontWeight: fontWeightMedium,
    fontSize: '1rem',
    lineHeight: 1.4,
    letterSpacing: '0.1px',
    color: themeColor.palette.text.secondary,
  },

  body1: {
    ...WorkSansFont,
    fontSize: '1rem',
    lineHeight: 1.5,
    letterSpacing: '0.15px',
  },
  // Emphasised body1, e.g. a total or other figure to stand out from rows.
  bodyBold: {
    ...WorkSansFont,
    fontWeight: fontWeightBold,
    fontSize: '1rem',
    lineHeight: 1.5,
    letterSpacing: '0.15px',
  },
  body2: {
    ...WorkSansFont,
    fontSize: '0.875rem',
    lineHeight: 1.43,
    letterSpacing: '0.17px',
  },

  caption: {
    ...WorkSansFont,
    fontSize: '0.75rem',
    lineHeight: 1.66,
    letterSpacing: '0.4px',
    color: themeColor.palette.text.secondary,
  },

  overline: {
    ...WorkSansFont,
    fontSize: '0.75rem',
    lineHeight: 2.66,
    letterSpacing: '1px',
    textTransform: 'uppercase' as const,
    color: themeColor.palette.text.secondary,
  },

  button: {
    ...WorkSansFont,
    fontSize: '1rem',
    lineHeight: 1.5,
    letterSpacing: '0.15px',
    textTransform: 'none' as const,
  },

  inputLabel: {
    ...WorkSansFont,
    fontSize: '0.9375rem',
    lineHeight: 1.2,
    letterSpacing: '0.15px',
  },
  inputValue: {
    ...WorkSansFont,
    fontSize: '1rem',
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },

  bodyMd: {
    ...WorkSansFont,
    fontSize: '1rem',
    lineHeight: 1.5,
  },
  bodyXs: {
    ...WorkSansFont,
    fontSize: '0.75rem',
    lineHeight: 1.5,
  },
  bodyXsMedium: {
    ...WorkSansFont,
    fontWeight: fontWeightMedium,
    fontSize: '0.75rem',
    lineHeight: 1.5,
  },
  bodySm: {
    ...WorkSansFont,
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  bodySmMedium: {
    ...WorkSansFont,
    fontWeight: fontWeightMedium,
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  bodySmSemibold: {
    ...WorkSansFont,
    fontWeight: fontWeightSemiBold,
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  bodySmBold: {
    ...WorkSansFont,
    fontWeight: fontWeightBold,
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  bodyMediumWeight: {
    ...WorkSansFont,
    fontWeight: fontWeightMedium,
    fontSize: '1rem',
    lineHeight: 1.5,
  },
  bodyLg: {
    ...WorkSansFont,
    fontSize: '1.125rem',
    lineHeight: 1.5,
  },
  bodyLgMedium: {
    ...WorkSansFont,
    fontWeight: fontWeightMedium,
    fontSize: '1.125rem',
    lineHeight: 1.5,
  },
  bodyXlSemibold: {
    ...WorkSansFont,
    fontWeight: fontWeightSemiBold,
    fontSize: '1.5rem',
    lineHeight: 1.5,
  },
  bodyXlBold: {
    ...WorkSansFont,
    fontWeight: fontWeightBold,
    fontSize: '1.5rem',
    lineHeight: 1.5,
  },
  bodyLgBold: {
    ...WorkSansFont,
    fontWeight: fontWeightBold,
    fontSize: '1.125rem',
    lineHeight: 1.5,
  },
  headingXs: {
    ...LoraFont,
    fontSize: '1.25rem',
    lineHeight: 1.3,
  },
  headingSm: {
    ...LoraFont,
    fontSize: '1.5rem',
    lineHeight: 1.2,
  },
  headingMd: {
    ...LoraFont,
    fontSize: '2.125rem',
    lineHeight: 1.2,
  },
  headingLg: {
    ...LoraFont,
    fontSize: '2.5rem',
    lineHeight: 1.1,
  },
};
